import React from "react";
import { useHistory, useParams } from "react-router-dom";

import { KintoneExportReport } from "@graffer-inc/cloud-gov-core";

import { useAuthContextV2 } from "../../../../../auth-state";
import { KintoneExportSettingDetailContent } from "../../../../organisms/kintone-export/kintone-export-setting-detail-content";
import { SmartApplyR } from "../../../smart-apply/route";
import {
  useKintoneExportSettingDetailPage,
  useKintoneFieldsByExportSetting,
} from "../page-hooks/kintone-export-setting-detail/hook";
import { KintoneExportR } from "../routes";
import { KintoneExportTemplate } from "../template";

export const KintoneExportSettingDetailPage: React.FC = () => {
  const { kintoneExportSettingId } = useParams<{
    kintoneExportSettingId: string;
  }>();
  const { auth } = useAuthContextV2();

  const {
    pageContent,
    updateKintoneExportSetting,
    manualExportSubmission,
    deleteKintoneExportedRecord,
    currentExportedRecordsPage,
    setCurrentExportedRecordsPage,
    lastExportedRecordsPage,
    currentExportReportsPage,
    setCurrentExportReportsPage,
    lastExportReportsPage,
  } = useKintoneExportSettingDetailPage({
    tenantId: auth.data?.orgId,
    kintoneExportSettingId,
  });

  // 設定の補助のために kintone のフィールドを取得する。
  // kintone の API トークンが無効であった場合でも画面を描画できるように、
  // グラファーの設定データとはあえてクエリを分離している。
  const kintoneFormFieldsInfo = useKintoneFieldsByExportSetting({
    kintoneExportSetting: pageContent?.kintoneExportSetting,
  });

  const history = useHistory();
  const moveToReportDetail = React.useCallback(
    (kintoneExportReportId: KintoneExportReport["id"]) => {
      history.push(
        KintoneExportR.KintoneExportReportDetail.link({
          kintoneExportSettingId,
          kintoneExportReportId,
        }),
      );
    },
    [history],
  );

  return (
    <KintoneExportTemplate
      breadcrumbs={[
        {
          to: KintoneExportR.KintoneExportSettings.link({}),
          title: "連携設定一覧",
        },
        {
          to: KintoneExportR.KintoneExportSettingDetail.link({
            kintoneExportSettingId,
          }),
          title:
            pageContent?.kintoneExportSetting.name ?? kintoneExportSettingId,
        },
      ]}
    >
      {!!pageContent && (
        <KintoneExportSettingDetailContent
          kintoneExportReports={pageContent.kintoneExportReports}
          kintoneExportSetting={pageContent.kintoneExportSetting}
          kintoneExportedRecords={pageContent.kintoneExportedRecords}
          procedure={pageContent.procedure}
          kintoneFormFieldsInfo={kintoneFormFieldsInfo}
          procedureDetailLink={`${SmartApplyR.ProcedureDetail.link({ id: pageContent.procedure.id })}?tab=FORM`}
          onClickUpdate={updateKintoneExportSetting}
          onClickExport={manualExportSubmission}
          onClickDeleteKintoneExportedRecord={deleteKintoneExportedRecord}
          moveToReportDetail={moveToReportDetail}
          currentExportedRecordsPage={currentExportedRecordsPage}
          setCurrentExportedRecordsPage={setCurrentExportedRecordsPage}
          lastExportedRecordsPage={lastExportedRecordsPage}
          currentExportReportsPage={currentExportReportsPage}
          setCurrentExportReportsPage={setCurrentExportReportsPage}
          lastExportReportsPage={lastExportReportsPage}
          // ServiceProvider の利便性向上を前提とした機能につき、いったん tenant user 向けには非表示とする。
          // json editor やコピー機能は求められてくると思うので、安全性やユースケースを考慮しつつ、解決策を検討する。
          showJsonEditor={false}
          showKintoneFieldsList={false}
        />
      )}
    </KintoneExportTemplate>
  );
};
